import { ResponseStatusEnum } from "@clockwise/schema";
import { CalendarPositioner } from "@clockwise/web-commons/src/components/calendar/";
import { ICalPositionable } from "@clockwise/web-commons/src/components/calendar/calendar-positioner/types";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { DateTime, Duration } from "luxon";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { IReduxState } from "../../../state/reducers/root.reducer";
import { useUpdateActiveEventDiff } from "../../chat-plus-calendar/util/ActiveDiffContext";
import { usePlannerContext } from "../Context";
import { hasIntervalAllDayLong } from "../ooo-events/utils/hasIntervalAllDayLong";
import { PlannerEventCard } from "../types";
import { hasCategoryOutOfOffice } from "../util/hasCategoryOutOfOffice";
import { PlannerEvent } from "./PlannerEvent";

type PlannerEventsProps = {
  dateTimes: DateTime[];
};

const MIN_DURATION = Duration.fromObject({ minutes: 15 });

export const PlannerEvents = ({ dateTimes }: PlannerEventsProps) => {
  const { hideDeclined, normalEvents: normalEventsNullable } = usePlannerContext();
  const updateActiveEvent = useUpdateActiveEvent();
  const updateActiveEventDiff = useUpdateActiveEventDiff();

  const highlightedSlotOnPlanner = useSelector(
    (state: IReduxState) => state.plannerHighlights.slot,
  );

  const eventIdsToFade = useMemo(
    () => (highlightedSlotOnPlanner?.additionalChanges || []).map((ac) => ac.event.externalEventId),
    [highlightedSlotOnPlanner?.additionalChanges],
  );

  const isHiddenBecauseDeclined = useMemo(
    () => isHiddenResponseStatus(hideDeclined ? [ResponseStatusEnum.Declined] : []),
    [hideDeclined],
  );

  const renderEvents = useMemo(
    () =>
      Object.values(normalEventsNullable ?? {})
        .flatMap((events) => events)
        .filter((event) => !hasCategoryOutOfOffice(event) || !hasIntervalAllDayLong(event))
        .filter(isHiddenBecauseDeclined),
    [isHiddenBecauseDeclined, normalEventsNullable],
  );

  const handleSelectEvent = useCallback(
    (calendarId: string, externalEventId: string) => {
      updateActiveEventDiff(null);
      updateActiveEvent({ calendarId, externalEventId });
    },
    [updateActiveEvent, updateActiveEventDiff],
  );

  const positionables: ICalPositionable[] = useMemo(
    () =>
      renderEvents.map((evnt) => ({
        // Note that we must use `positioner?.keyAddendum` for Schedule by Moving `Before` and `After` views
        // because `positioner.key`s alone are not unique, which leads to UI bugs
        // TODO (jimmy): let's fix `key` Linear AI-1422 it should be unique
        // and we separate the conflation by forcing downstream consumers to use `externalEventId` instead of `key`
        key: `${evnt.key}${evnt.keyAddendum ?? ""}`,
        interval: evnt.interval,
        render: ({ position, columnIndex }) => {
          return (
            <PlannerEvent
              key={evnt.key}
              card={evnt}
              onClick={handleSelectEvent}
              fadedWithAWandLabel={eventIdsToFade.includes(evnt?.externalEventId || "")}
              leftPad={hasCategoryOutOfOffice(evnt) && position.renderOrder > 0}
              popoverSide={position.left < 60 ? "right" : "left"} // Open on the left for Thursday and Friday, because of space constraints
              popoverPlacement={position.top > 75 ? "end" : "start"}
              position={position}
              columnIndex={columnIndex}
            />
          );
        },
      })),
    [eventIdsToFade, handleSelectEvent, renderEvents],
  );

  return (
    <CalendarPositioner
      dateTimes={dateTimes}
      conflictResolution="columnOverlap"
      gutters
      minDuration={MIN_DURATION}
      positionables={positionables}
    />
  );
};

const isHiddenResponseStatus = (hiddenResponseStatuses: (ResponseStatusEnum | "Proposed")[]) => (
  event: PlannerEventCard,
) => !hiddenResponseStatuses.includes(event.responseState);
